import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { eventService } from '../services/eventService';

const AssignmentContainer = styled.div`
  background: ${({ theme }) => theme.color.secondaryBg};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 8px;
  padding: 16px;
  margin-top: 1rem;
`;

const ActionButton = styled.button`
  background: ${props => props.variant === 'danger' ? '#e53e3e' : '#3182ce'};
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: ${props => props.variant === 'danger' ? '#c53030' : '#2c5282'};
  }
  
  &:disabled {
    background: #4a5568;
    cursor: not-allowed;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  margin-bottom: 8px;
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  color: ${({ theme }) => theme.color.darkerText};
  border-radius: 4px;
  outline: 0;

  &::placeholder {
    color: ${({ theme }) => theme.color.lighterText};
    opacity: 0.6;
  }

  &:focus {
    box-shadow: 0 0 0 0.2rem rgba(69, 105, 255, 0.35);
  }
`;

const SuggestionsList = styled.ul`
  margin: 0;
  margin-top: 4px;
  padding: 0;
  list-style: none;
  background: ${({ theme }) => theme.color.background};
  border: 1px solid ${({ theme }) => theme.color.border};
  border-radius: 4px;
  max-height: 180px;
  overflow-y: auto;
`;

const SuggestionItem = styled.li`
  padding: 6px 10px;
  cursor: pointer;
  color: ${({ theme }) => theme.color.darkerText};

  &:hover {
    background: ${({ theme }) => theme.color.secondaryBg};
  }
`;

const StreamerAssignmentManager = ({ eventSlug, onAssign }) => {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchSuggestions = useCallback(async (term) => {
    if (!term || term.length < 3) {
      setSuggestions([]);
      return;
    }
    try {
      setSearching(true);
      const res = await eventService.searchUsers(term);
      setSuggestions(res.users || []);
    } catch (err) {
      console.error('Error searching users:', err);
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleAssign = async () => {
    if (!username) return toast.error('Enter a username');
    setLoading(true);
    try {
        const result = await eventService.assignStreamer(eventSlug, { username });
        onAssign({ username: result.username || username });
        setUsername('');
        toast.success(`Invited streamer: ${username}`);
    } catch (err) {
        toast.error(err.response?.data?.error || "Failed to invite streamer");
    } finally {
      setLoading(false);
    }
  };

  // Trigger search when typing (>= 3 chars)
  useEffect(() => {
    fetchSuggestions(username);
  }, [username, fetchSuggestions]);

  return (
    <AssignmentContainer>
      <h5>Invite Player/Streamer</h5>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Input
            type="text"
            placeholder="Type at least 3 letters to search (e.g. johndoe)" 
            value={username}
            onChange={e => setUsername(e.target.value)}
            onKeyPress={e => e.key === 'Enter' && handleAssign()}
          />
          <ActionButton onClick={handleAssign} disabled={loading}>
            {loading ? 'Inviting...' : 'Invite'}
              </ActionButton>
            </div>
        {searching && <p style={{fontSize: '0.75rem', color: 'inherit', opacity: 0.7}}>Searching users...</p>}
        {suggestions.length > 0 && (
          <SuggestionsList>
            {suggestions.map(user => (
              <SuggestionItem
                key={user.id}
                onClick={() => {
                  setUsername(user.username);
                  setSuggestions([]);
                }}
              >
                {user.username}
              </SuggestionItem>
            ))}
          </SuggestionsList>
        )}
      </div>
      <p style={{fontSize: '0.8rem', color: 'inherit', opacity: 0.7, marginTop: '0.5rem'}}>
        * Invite players to compete AND stream their POV (Multi-POV). Each player can broadcast their own gameplay.
      </p>
    </AssignmentContainer>
  );
};

export default StreamerAssignmentManager;
